import { CURRENCY_CODE } from '../constants';

const API_URL = 'https://open.er-api.com/v6/latest/USD';
const CACHE_KEY = 'meda_exchange_rates_kzt_v5';
const MANUAL_RATES_KEY = 'meda_exchange_rates_manual_v1';
const CACHE_TTL_MS = 60 * 60 * 1000;
// Döviz bürosu satış kuruna yaklaşmak için API mid-market değerine eklenen sabit marj (₸)
const USD_RATE_MARGIN = 6;
const EUR_RATE_MARGIN = 8;

export const DEFAULT_EXCHANGE_RATES = { USD: 1, EUR: 1 };

const LOCAL_CURRENCY_CODES = new Set(['TRY', 'KZT', 'TL', CURRENCY_CODE]);

let memoryCache = null;
let autoRates = { ...DEFAULT_EXCHANGE_RATES };
let manualOverrides = readManualOverridesFromStorage();
let inflightPromise = null;
const subscribers = new Set();
let refreshTimer = null;

function readManualOverridesFromStorage() {
  try {
    const raw = localStorage.getItem(MANUAL_RATES_KEY);
    if (!raw) return { USD: null, EUR: null };
    const parsed = JSON.parse(raw);
    return {
      USD: normalizeManualRate(parsed?.USD),
      EUR: normalizeManualRate(parsed?.EUR)
    };
  } catch (_) {
    return { USD: null, EUR: null };
  }
}

function writeManualOverridesToStorage() {
  try {
    localStorage.setItem(MANUAL_RATES_KEY, JSON.stringify(manualOverrides));
  } catch (_) {
    // localStorage kullanılamıyorsa oturum boyunca bellekte tutulur
  }
}

function normalizeManualRate(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(parseFloat(value).toFixed(2));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

const getEffectiveRates = () => ({
  USD: manualOverrides.USD ?? autoRates.USD,
  EUR: manualOverrides.EUR ?? autoRates.EUR
});

export const isLocalCurrency = (currency) => {
  if (!currency) return true;
  return LOCAL_CURRENCY_CODES.has(currency);
};

export const getUsdToKztRate = (exchangeRates) => parseFloat(exchangeRates?.USD) || 1;

export const getManualExchangeOverrides = () => ({ ...manualOverrides });

export const isManualExchangeRate = (currency) => manualOverrides[currency] != null;

export const setManualExchangeRate = (currency, value) => {
  if (currency !== 'USD' && currency !== 'EUR') return getEffectiveRates();

  manualOverrides = {
    ...manualOverrides,
    [currency]: normalizeManualRate(value)
  };
  writeManualOverridesToStorage();
  const rates = getEffectiveRates();
  notifySubscribers(rates);
  return rates;
};

export const clearManualExchangeRate = (currency) => setManualExchangeRate(currency, null);

export const parseExchangeRatesFromApi = (data) => {
  if (!data?.rates?.KZT || !data?.rates?.EUR) return null;

  const usdToKzt = parseFloat(data.rates.KZT);
  const eurToKzt = usdToKzt / parseFloat(data.rates.EUR);

  if (!Number.isFinite(usdToKzt) || !Number.isFinite(eurToKzt)) return null;

  return {
    USD: Number((usdToKzt + USD_RATE_MARGIN).toFixed(2)),
    EUR: Number((eurToKzt + EUR_RATE_MARGIN).toFixed(2))
  };
};

export const convertForeignToLocal = (price, currency, rates = DEFAULT_EXCHANGE_RATES) => {
  const amount = parseFloat(price) || 0;
  if (isLocalCurrency(currency)) return amount;
  const rate = parseFloat(rates[currency]) || 1;
  return amount * rate;
};

const readCache = () => {
  if (memoryCache && Date.now() - memoryCache.fetchedAt < CACHE_TTL_MS) {
    return memoryCache.rates;
  }

  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.rates && Date.now() - parsed.fetchedAt < CACHE_TTL_MS) {
      memoryCache = parsed;
      return parsed.rates;
    }
  } catch (_) {
    // sessionStorage kullanılamıyorsa API'den devam et
  }

  return null;
};

const writeCache = (rates) => {
  autoRates = { ...rates };
  memoryCache = { rates: autoRates, fetchedAt: Date.now() };
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(memoryCache));
  } catch (_) {
    // sessionStorage dolu/engelli ise sadece bellek önbelleği kullan
  }
};

const notifySubscribers = (rates) => {
  subscribers.forEach((listener) => {
    try {
      listener(rates);
    } catch (_) {
      // Abone hatası diğer dinleyicileri etkilemesin
    }
  });
};

const publishEffectiveRates = () => {
  notifySubscribers(getEffectiveRates());
};

const getMsUntilCacheExpiry = () => {
  const fetchedAt = memoryCache?.fetchedAt;
  if (!fetchedAt) return 0;
  return Math.max(0, CACHE_TTL_MS - (Date.now() - fetchedAt));
};

const clearRefreshTimer = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
};

const scheduleNextRefresh = () => {
  clearRefreshTimer();
  if (subscribers.size === 0) return;

  const delay = getMsUntilCacheExpiry() || CACHE_TTL_MS;
  refreshTimer = setTimeout(async () => {
    if (subscribers.size === 0) return;

    try {
      await refreshExchangeRates({ preserveManual: true });
    } catch (_) {
      // Son bilinen kur abonelere zaten iletilmiş durumda
    }

    scheduleNextRefresh();
  }, delay);
};

const requestExchangeRatesFromApi = () => {
  inflightPromise = fetch(API_URL)
    .then((response) => response.json())
    .then((data) => {
      const rates = parseExchangeRatesFromApi(data) || DEFAULT_EXCHANGE_RATES;
      writeCache(rates);
      return autoRates;
    })
    .catch(() => {
      autoRates = { ...DEFAULT_EXCHANGE_RATES };
      return autoRates;
    })
    .finally(() => {
      inflightPromise = null;
    });

  return inflightPromise;
};

export const fetchExchangeRates = async ({ forceRefresh = false } = {}) => {
  if (!forceRefresh) {
    const cached = readCache();
    if (cached) {
      autoRates = { ...cached };
      return getEffectiveRates();
    }
  }

  await requestExchangeRatesFromApi();
  return getEffectiveRates();
};

export const invalidateExchangeRatesCache = () => {
  memoryCache = null;
  inflightPromise = null;
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch (_) {
    // sessionStorage kullanılamıyorsa bellek önbelleği yeterli
  }
};

export const refreshExchangeRates = async ({ preserveManual = true } = {}) => {
  if (!preserveManual) {
    manualOverrides = { USD: null, EUR: null };
    writeManualOverridesToStorage();
  }

  invalidateExchangeRatesCache();
  clearRefreshTimer();
  await fetchExchangeRates({ forceRefresh: true });
  publishEffectiveRates();
  scheduleNextRefresh();
  return getEffectiveRates();
};

export const subscribeExchangeRates = (listener) => {
  subscribers.add(listener);

  fetchExchangeRates()
    .then((rates) => listener(rates))
    .catch(() => listener(getEffectiveRates()));

  scheduleNextRefresh();

  return () => {
    subscribers.delete(listener);
    if (subscribers.size === 0) {
      clearRefreshTimer();
    } else {
      scheduleNextRefresh();
    }
  };
};

export const formatExchangeRatesForDisplay = (rates) => ({
  USD: Number(rates.USD).toFixed(2),
  EUR: Number(rates.EUR).toFixed(2)
});
